/**
 * FSM Viewer — Dev/Admin page
 *
 * Fetches /api/domains and renders each domain's FSM as a real directed
 * graph using React Flow + dagre layout. Stages become nodes, transitions
 * become labelled edges. Self-loops and back-edges render correctly.
 *
 * Only shown in dev or to admin users.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from '@dagrejs/dagre';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Users,
  Layers,
  GitBranch,
  Info,
} from 'lucide-react';

// ── Colour palette per template_type ────────────────────────────────────────
const TYPE_COLORS = {
  state_routine:      { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Intra-state' },
  state_investigation:{ bg: 'bg-purple-100', text: 'text-purple-700', label: 'Investigation' },
  transition_action:  { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Transition boundary' },
};

const NODE_WIDTH = 200;
const NODE_HEIGHT = 90;

// Pick a layout direction based on graph complexity: TB handles many
// back-edges more cleanly than LR (where reverse arcs have to loop over
// or under the entire row of nodes).
function pickRankDir(stages, transitions) {
  const backEdges = transitions.filter((t) => {
    const a = stages.findIndex((s) => s.key === t.from);
    const b = stages.findIndex((s) => s.key === t.to);
    return a >= 0 && b >= 0 && b < a;
  }).length;
  return backEdges >= 3 || stages.length >= 5 ? 'TB' : 'LR';
}

// ── Custom stage node (React Flow node type) ────────────────────────────────
// Handles on all four sides so dagre's chosen edge endpoints always have
// somewhere to attach. React Flow picks the closest handle per edge.
function StageRFNode({ data }) {
  const types = data.template_types || [];
  const handleStyle = { background: '#94a3b8', width: 6, height: 6 };
  return (
    <div
      onClick={data.onClick}
      className={`relative flex flex-col gap-1 px-3 py-2 rounded-xl border-2 text-left cursor-pointer transition-all bg-white ${
        data.isSelected
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-gray-300 hover:border-blue-300 hover:shadow-sm'
      }`}
      style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
    >
      <Handle id="t" type="target" position={Position.Top} style={handleStyle} />
      <Handle id="l" type="target" position={Position.Left} style={handleStyle} />
      <span className="text-sm font-semibold text-gray-900 leading-tight">{data.label}</span>
      {data.description && (
        <span className="text-[11px] text-gray-500 line-clamp-2 leading-snug">{data.description}</span>
      )}
      <div className="flex flex-wrap gap-1 mt-0.5">
        {types.map((t) => {
          const c = TYPE_COLORS[t] || { bg: 'bg-gray-100', text: 'text-gray-600', label: t };
          return (
            <span key={t} className={`text-[9px] px-1 py-0.5 rounded font-medium ${c.bg} ${c.text}`}>
              {c.label}
            </span>
          );
        })}
      </div>
      <Handle id="b" type="source" position={Position.Bottom} style={handleStyle} />
      <Handle id="r" type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

const nodeTypes = { stage: StageRFNode };

// ── Dagre layout helper ──────────────────────────────────────────────────────
function layoutGraph(nodes, edges, rankdir) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir, nodesep: 60, ranksep: 110, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  // dagre dislikes self-loops in layout — skip them, render via React Flow's
  // built-in self-loop edge support. Pass an explicit label object (dagre 3.x
  // throws if the edge label is undefined when computing label points).
  edges.forEach((e) => {
    if (e.source !== e.target) {
      g.setEdge(e.source, e.target, { minlen: 1, weight: 1, width: 0, height: 0, labelpos: 'r', labeloffset: 0 });
    }
  });
  dagre.layout(g);

  const isHorizontal = rankdir === 'LR' || rankdir === 'RL';
  return nodes.map((n) => {
    const { x, y } = g.node(n.id);
    return {
      ...n,
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
    };
  });
}

// ── FSM diagram (React Flow canvas) ──────────────────────────────────────────
function FSMDiagram({ domain, selectedStage, onSelectStage }) {
  const stages = domain.stages || [];
  const transitions = domain.transitions || [];

  const rankdir = useMemo(() => pickRankDir(stages, transitions), [stages, transitions]);

  const { rfNodes, rfEdges } = useMemo(() => {
    const baseNodes = stages.map((s) => ({
      id: s.key,
      type: 'stage',
      position: { x: 0, y: 0 },
      data: {
        label: s.label,
        description: s.description,
        template_types: s.template_types,
        isSelected: selectedStage?.key === s.key,
        onClick: () => onSelectStage(s),
      },
    }));

    const edges = transitions.map((t, i) => {
      const involved = selectedStage
        ? t.from === selectedStage.key || t.to === selectedStage.key
        : false;
      const dimmed = selectedStage && !involved;
      const isSelfLoop = t.from === t.to;
      const colour = dimmed ? '#cbd5e1' : involved ? '#2563eb' : '#475569';
      return {
        id: `e-${i}-${t.from}-${t.to}-${t.trigger}`,
        source: t.from,
        target: t.to,
        label: t.trigger,
        labelStyle: {
          fontSize: 10,
          fill: dimmed ? '#94a3b8' : '#1e293b',
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontWeight: 500,
        },
        labelBgStyle: { fill: '#ffffff', fillOpacity: 0.92 },
        labelBgPadding: [6, 3],
        labelBgBorderRadius: 4,
        type: isSelfLoop ? 'default' : 'smoothstep',
        animated: involved,
        style: { stroke: colour, strokeWidth: involved ? 2.5 : 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: colour, width: 18, height: 18 },
        zIndex: involved ? 10 : 1,
      };
    });

    const laidOut = layoutGraph(baseNodes, edges, rankdir);
    return { rfNodes: laidOut, rfEdges: edges };
  }, [stages, transitions, selectedStage, onSelectStage, rankdir]);

  if (!stages.length) {
    return <p className="text-sm text-gray-400 italic">No stages defined.</p>;
  }

  // TB layouts need vertical room proportional to stage count; LR scales
  // mostly horizontally so a fixed height is fine.
  const height = rankdir === 'TB'
    ? Math.max(360, Math.min(720, 160 + stages.length * 80))
    : Math.max(280, Math.min(520, 160 + stages.length * 30));

  return (
    <div
      style={{ height }}
      className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden"
    >
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

// ── Transition list (kept for the textual breakdown below the diagram) ──────
function TransitionList({ transitions, stages, selectedStage }) {
  const stageMap = Object.fromEntries((stages || []).map((s) => [s.key, s.label]));

  const filtered = selectedStage
    ? transitions.filter((t) => t.from === selectedStage.key || t.to === selectedStage.key)
    : transitions;

  if (!filtered.length) {
    return (
      <p className="text-sm text-gray-400 italic">No transitions for this state.</p>
    );
  }

  return (
    <div className="space-y-2">
      {filtered.map((t, i) => (
        <div
          key={i}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${
            selectedStage?.key === t.from
              ? 'bg-blue-50 border-blue-200'
              : selectedStage?.key === t.to
              ? 'bg-green-50 border-green-200'
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <span className="font-medium text-gray-700 min-w-[90px]">
            {stageMap[t.from] || t.from}
          </span>
          <span className="text-gray-400">→</span>
          <span className="font-medium text-gray-700 min-w-[90px]">
            {stageMap[t.to] || t.to}
          </span>
          <span className="text-gray-500 text-xs flex-1 truncate" title={t.label}>
            {t.label}
          </span>
          <code className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
            {t.trigger}
          </code>
        </div>
      ))}
    </div>
  );
}

// ── Domain card ──────────────────────────────────────────────────────────────
function DomainCard({ domain }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  const handleStageClick = useCallback((stage) => {
    setSelectedStage((prev) => (prev?.key === stage.key ? null : stage));
  }, []);

  const stageCount = domain.stages?.length || 0;
  const transitionCount = domain.transitions?.length || 0;
  const hasFullFSM = transitionCount > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <GitBranch size={18} className="text-blue-500 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900">{domain.label}</h3>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{domain.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          <span className="text-xs text-gray-400">{stageCount} stages</span>
          {hasFullFSM ? (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              {transitionCount} transitions
            </span>
          ) : (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
              stages only
            </span>
          )}
          {expanded ? (
            <ChevronDown size={16} className="text-gray-400" />
          ) : (
            <ChevronRight size={16} className="text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-5">
          {/* Roles */}
          {domain.roles?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Users size={14} className="text-gray-400 flex-shrink-0" />
              {domain.roles.map((r) => (
                <span key={r} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                  {r}
                </span>
              ))}
            </div>
          )}

          {/* Diagram */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              State Flow
            </p>
            <FSMDiagram
              domain={domain}
              selectedStage={selectedStage}
              onSelectStage={handleStageClick}
            />
            {selectedStage && (
              <p className="text-xs text-blue-600 mt-2">
                Click <strong>{selectedStage.label}</strong> again to deselect.
                Highlighting transitions involving it.
              </p>
            )}
          </div>

          {/* Transitions */}
          {hasFullFSM && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                {selectedStage ? `Transitions involving ${selectedStage.label}` : 'All Transitions'}
              </p>
              <TransitionList
                transitions={domain.transitions}
                stages={domain.stages}
                selectedStage={selectedStage}
              />
            </div>
          )}

          {!hasFullFSM && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <Info size={15} className="flex-shrink-0 mt-0.5" />
              <span>Full FSM YAML not yet defined for this domain. Stages only.</span>
            </div>
          )}

          {/* Template type legend */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Template Types in this Domain
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TYPE_COLORS).map(([key, c]) => (
                <span key={key} className={`text-xs px-2 py-1 rounded font-medium ${c.bg} ${c.text}`}>
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Legend ───────────────────────────────────────────────────────────────────
function Legend() {
  const types = [
    { key: 0, label: 'Type 0 — Triggered', desc: 'Fires on external event (schedule, webhook, email). No manual Run.', color: 'bg-rose-100 text-rose-700' },
    { key: 1, label: 'Type 1 — Intra-state', desc: 'Internal process while entity is in this state. FSM state is precondition.', color: 'bg-blue-100 text-blue-700' },
    { key: 2, label: 'Type 2 — Transition boundary', desc: 'Output justifies the state change. Human approval gate expected.', color: 'bg-amber-100 text-amber-700' },
    { key: 3, label: 'Type 3 — Ad-hoc', desc: 'No FSM dependency. Pure utility across domains.', color: 'bg-gray-100 text-gray-700' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Layers size={16} className="text-gray-500" />
        Template Type Legend
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {types.map((t) => (
          <div key={t.key} className="flex items-start gap-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 mt-0.5 ${t.color}`}>
              Type {t.key}
            </span>
            <div>
              <p className="text-sm font-medium text-gray-800">{t.label.split(' — ')[1]}</p>
              <p className="text-xs text-gray-500">{t.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function FSMViewer() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/domains');
      setDomains(res.data?.domains || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load domain FSMs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const withFSM = domains.filter((d) => (d.transitions?.length || 0) > 0);
  const withoutFSM = domains.filter((d) => !(d.transitions?.length > 0));

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Domain FSM Registry</h1>
            <p className="text-sm text-gray-500 mt-1">
              {domains.length} domains — {withFSM.length} with full FSM definitions
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
            <RefreshCw size={18} className="animate-spin" />
            <span>Loading FSMs...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-4">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-4">
            <Legend />

            {withFSM.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide pt-2">
                  Full FSM Definitions ({withFSM.length})
                </h2>
                {withFSM.map((d) => <DomainCard key={d.key} domain={d} />)}
              </>
            )}

            {withoutFSM.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide pt-2">
                  Stages Only — FSM YAML pending ({withoutFSM.length})
                </h2>
                {withoutFSM.map((d) => <DomainCard key={d.key} domain={d} />)}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
